import { TenantAssociation } from './TenantAssociation.model';
import { UserContactInfo } from './UserContactInfo.model';
import { UserCurrentRole } from './UserCurrentRole.model';
import { UserDefaultRole } from './UserDefaultRole.model';
import { UserRole, UserRoleEnum } from './UserRole.model';

/**
 * Represents a user.
 */
export interface GalenDataUser {
  /**
   * The identifier of the user. Its auto-generated on create if not provided.
   */
  userId: string;

  /**
   * The first name of the user.
   */
  firstName: string;

  /**
   * The middle name of the user.
   */
  middleName?: string;

  /**
   * The last name of the user.
   */
  lastName: string;

  /**
   * The full name of the user.
   */
  fullName?: string;

  /**
   * The email address of the user.
   */
  emailAddress?: string;

  /**
   * The tenant to which the user belongs.
   */
  tenant: TenantAssociation;

  /**
   * The contact information for the user.
   */
  contactInfo?: UserContactInfo;

  /**
   * The list of roles for this user.
   */
  roles: [UserRole];

  /**
   * The current role used by this user.
   */
  currentRole?: UserCurrentRole;

  /**
   * The default role for this user.
   */
  defaultRole?: UserDefaultRole;

  /**
   * The date of birth for the user.
   */
  dateOfBirth?: string; // Date format

  /**
   * The height in inches for the user.
   */
  height?: number;

  /**
   * The weight in pounds for the user.
   */
  weight?: number;

  /**
   * The unique identifier of a patient (if this user is a patient).
   */
  patientId?: string;

  /**
   * The IETF language code for the user's language.
   */
  language?: string;

  /**
   * The IANA compatible time zone ID of the user.
   */
  timeZone?: string;

  /**
   * The current status of the user.
   */
  status?: UserStatusEnum;

  /**
   * Whether the user has agreed to Galen's terms of use.
   */
  acceptedTermsOfUse?: boolean;

  /**
   * Whether the user has agreed to the manufacturer's terms of use.
   */
  acceptedTermsOfUseForTenant?: boolean;

  /**
   * The number of failed login attempts since the last successful login.
   */
  failedLoginAttempts?: number;

  /**
   * Whether the user is currently locked out.
   */
  lockOut?: boolean;

  /**
   * If locked out, the date and time the user was locked out.
   */
  lockOutDate?: Date;

  /**
   * Whether the user has Multi-Factor Authentication (MFA) enabled.
   */
  mfaEnable?: boolean;

  /**
   * Whether the user's password has expired.
   */
  passwordExpired?: boolean;

  /**
   * The identifier of the user who created this user.
   */
  createdBy?: string;

  /**
   * The date/time at which this user was created.
   */
  createdOn?: Date;

  /**
   * The identifier of the user who last updated this user.
   */
  lastUpdatedBy?: string;

  /**
   * The date/time at which this user was last updated.
   */
  lastUpdatedOn?: Date;

  /**
   * Whether this user can view sensitive data.
   */
  canViewSensitiveData?: boolean;

  /**
   * Whether the user can login. If this is false, the user cannot login to the system.
   * If this is false, the email address is not required, and passwords are not saved (if provided).
   */
  loggableAccount?: boolean;

  /**
   * The date/time at which this user last logged on to the system.
   */
  lastLoginOn?: string; // Date format

  sites?: string[];
}

export interface GalenDataUserColumn {
  name: string;
  role?: UserRoleEnum;
  emailAddress?: string;
  patientId?: string;
  supplierId?: string;
  practiceId?: string;
  mfaEnable?: boolean;
  loggableAccount: boolean;
}

/**
 * Enumeration for user status.
 */
export enum UserStatusEnum {
  Active = 'Active',
  Inactive = 'Inactive',
  Pending = 'Pending',
}
